import { Button, Dialog, Field, Input, InputArea } from '@cloudflare/kumo';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppToast } from '../hooks/useAppToast';
import { CategoryInput, Category } from '../types';
import { apiService } from '../services/api';

const categorySchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  cor: z.string().min(1, 'Cor é obrigatória'),
  descricao: z.string().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface CategoryFormProps {
  category?: Category;
  onSuccess: () => void;
  onCancel: () => void;
}

const PALETTE = [
  '#00A35C', // LeafyGreen green
  '#016BF8', // LeafyGreen blue
  '#B45AF2', // LeafyGreen purple
  '#FFC010', // LeafyGreen yellow
  '#DB3030', // LeafyGreen red
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#112733', // LeafyGreen dark
];

export function CategoryForm({ category, onSuccess, onCancel }: CategoryFormProps) {
  const toast = useAppToast();
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      nome: category?.nome || '',
      cor: category?.cor || '#00A35C',
      descricao: category?.descricao || '',
    },
  });

  const selectedColor = watch('cor');

  const onSubmit = async (data: CategoryFormData) => {
    try {
      const input: CategoryInput = {
        nome: data.nome,
        cor: data.cor,
        descricao: data.descricao || undefined,
      };

      if (category) {
        await apiService.updateCategory(category.id, input);
        toast.success('Categoria atualizada com sucesso');
      } else {
        await apiService.createCategory(input);
        toast.success('Categoria criada com sucesso');
      }

      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar categoria');
    }
  };

  return (
    <Dialog.Root open={true} onOpenChange={(o: boolean) => { if (!o) (onCancel)(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">{category ? 'Editar Categoria' : 'Nova Categoria'}</h3>}</Dialog.Title>

      <p className="!text-[var(--text-muted)] mb-6">
        {category ? 'Atualize as informações da categoria' : 'Crie uma categoria para organizar seus contatos'}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <Field label="Nome" error={errors.nome?.message ? { message: errors.nome?.message, match: true } : undefined}>
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <Input onChange={(e) => field.onChange(e.target.value)} aria-label="Nome" value={field.value} placeholder="Digite o nome da categoria" />
            )}
          />
        </Field>

        <div>
          <p className="font-semibold !text-[var(--text)] mb-2">Cor</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {PALETTE.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setValue('cor', color)}
                className="w-10 h-10 rounded-lg transition-all hover:scale-110 focus:outline-none"
                style={{
                  backgroundColor: color,
                  boxShadow: selectedColor === color ? `0 0 0 3px var(--surface), 0 0 0 5px ${color}` : 'none',
                }}
                aria-label={`Selecionar cor ${color}`}
                aria-pressed={selectedColor === color}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <input
              id="cor"
              type="color"
              {...register('cor')}
              className="w-12 h-10 border border-[var(--border)] rounded cursor-pointer"
            />
            <p className="!text-[var(--text-muted)]">Ou escolha uma cor personalizada</p>
          </div>
          {errors.cor && (
            <p className="text-[var(--crm-text-error)] text-sm mt-1">{errors.cor.message}</p>
          )}
        </div>

        <Field label="Descrição" description="Opcional">
          <Controller
            name="descricao"
            control={control}
            render={({ field }) => (
              <InputArea onChange={(e) => field.onChange(e.target.value)} aria-label="Descrição" value={field.value ?? ''} placeholder="Descrição opcional da categoria" />
            )}
          />
        </Field>

        <div className="flex gap-3 pt-2 justify-end">
          <Button variant="secondary" type="button" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
            {category ? 'Salvar Alterações' : 'Criar Categoria'}
          </Button>
        </div>
      </form>
    
</Dialog>
</Dialog.Root>
  );
}
